// B-47 — one variable: its readable name and value, and its details behind a toggle.
//
// Closed by default, so the grid stays as compact as it was; the details open in place under
// the value. A real <button> with `aria-expanded`/`aria-controls`, so it works from a keyboard
// and reads correctly to a screen reader. Nothing here knows what a variable is — the caller
// hands it `detalles`, rows of `{ etiqueta, valor }` (see `utils/cierreNombres.js`).

import { useId, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@mantis-sas/ui";

export function VariableDesplegable({
  nombre,
  valor,
  detalles = [],
  tono = "normal",
  // A figure: drawn right-aligned so the cards of one grid line their digits up. Off by default,
  // so the worksheet ("Trabajar a mano") keeps its layout.
  numerico = false,
  className,
  children,
}) {
  const [abierto, setAbierto] = useState(false);
  const idDetalles = useId();
  const tieneDetalles = detalles.length > 0;

  return (
    <div className={cn("min-w-0 rounded-[8px] bg-brinks-surface px-2 py-1.5", className)}>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        disabled={!tieneDetalles}
        aria-expanded={abierto}
        aria-controls={idDetalles}
        title={tieneDetalles ? (abierto ? "Ocultar detalles" : "Ver detalles") : undefined}
        className={cn(
          "flex w-full min-w-0 items-start justify-between gap-1.5 text-left",
          tieneDetalles ? "cursor-pointer" : "cursor-default"
        )}
      >
        <span className="min-w-0 flex-1">
          {/* Wraps instead of truncating: a readable name is longer than a key, and a cut name
              is back to being unreadable. */}
          <span className="block text-[10px] leading-tight text-brinks-muted-foreground">{nombre}</span>
          {children || (
            <span
              className={cn(
                "mt-0.5 block truncate text-[13px] font-semibold leading-tight tabular-nums",
                numerico && "text-right",
                tono === "apagado" ? "text-brinks-muted-foreground" : "text-brinks-foreground"
              )}
            >
              {valor}
            </span>
          )}
        </span>
        {tieneDetalles ? (
          <ChevronDown
            aria-hidden="true"
            className={cn("mt-0.5 h-3.5 w-3.5 shrink-0 text-brinks-muted-foreground transition-transform", abierto && "rotate-180")}
          />
        ) : null}
      </button>
      {abierto && tieneDetalles ? (
        <dl id={idDetalles} className="mt-1.5 space-y-1 border-t border-brinks-border pt-1.5">
          {detalles.map((fila) => (
            <div key={fila.etiqueta}>
              <dt className="text-[9.5px] font-semibold uppercase tracking-[0.04em] text-brinks-muted-foreground">
                {fila.etiqueta}
              </dt>
              <dd className="break-words text-[11px] leading-snug text-brinks-foreground">{fila.valor}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  );
}

// The same details, as an inline "Ver detalles" toggle — for a row that already has its own
// layout (the worksheet's label + input), where a whole card would be one box too many.
export function DetallesPlegables({ detalles = [], className }) {
  const [abierto, setAbierto] = useState(false);
  const idDetalles = useId();
  if (!detalles.length) return null;

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        aria-controls={idDetalles}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-brinks-muted-foreground hover:text-brinks-foreground"
      >
        {abierto ? "Ocultar detalles" : "Ver detalles"}
        <ChevronDown aria-hidden="true" className={cn("h-3 w-3 transition-transform", abierto && "rotate-180")} />
      </button>
      {abierto ? (
        <dl id={idDetalles} className="mt-1 space-y-1 rounded-[6px] bg-brinks-surface px-2 py-1.5">
          {detalles.map((fila) => (
            <div key={fila.etiqueta}>
              <dt className="text-[9.5px] font-semibold uppercase tracking-[0.04em] text-brinks-muted-foreground">
                {fila.etiqueta}
              </dt>
              <dd className="break-words text-[11px] leading-snug text-brinks-foreground">{fila.valor}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  );
}
